import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { CareerIcon } from 'atoms';
import { Colors } from 'context';
import { ICareerAction } from 'core';
import { careerColor, CTA_TEXT } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';
import { mobile } from 'services/ui';

import { actionText } from '../model/careerPlanText';

interface ICareerPlanAction {
  action: ICareerAction;
}

/**
 * Основное следующее действие (ТЗ 0071, п. 4.4; «визуальное упрощение», 5.4).
 *
 * Белая карточка с тонкой рамкой вместо жёлтого поля: насыщенный жёлтый на
 * экране один — у кнопки. Внутри подпись, название, метаданные («Lekcja 6 z 7 ·
 * 22 min · W trakcie»), ОДНА строка текста и кнопка.
 *
 * Строка текста — результат урока, если он есть, иначе польза действия. Так у
 * начатого урока не повторяется «dokończ, żeby otworzyć następną» (это уже
 * сказано состоянием «W trakcie»), а закрытое действие сохраняет причину.
 *
 * 🔴 Кнопка — ссылка: у действия есть адрес, и его должно быть можно открыть в
 * новой вкладке.
 */
export const CareerPlanAction = memo(({ action }: ICareerPlanAction) => {
  const { t } = useTranslation();
  const text = actionText(t, action);

  return (
    <PlanCard aria-labelledby="career-plan-action-title">
      <PlanLabel>{t('careerCenter.plan.nextTitle')}</PlanLabel>
      <PlanTitle id="career-plan-action-title">
        <CareerIcon name={text.icon} size={24} />
        <span>{text.title}</span>
      </PlanTitle>
      {text.meta.length ? <PlanMeta>{text.meta.join(' · ')}</PlanMeta> : null}
      {text.line ? <PlanText>{text.line}</PlanText> : null}
      <PlanCta to={action.route}>{text.cta}</PlanCta>
    </PlanCard>
  );
});

/* Общие части карточки — ими же собран блок «выберите цель» (CareerPlanGoal). */

export const PlanCard = styled.section`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 24px;
  border: 1px solid ${careerColor.line};
  border-radius: ${careerLayout.cardRadius}px;
  background: ${careerColor.bg};
  color: ${careerColor.text};
`;

export const PlanLabel = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

export const PlanTitle = styled.h2`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  color: ${careerColor.text};
  overflow-wrap: anywhere;
  ${careerType.actionTitle()};

  & > svg {
    flex: none;
    /* Иконка 24 по центру первой строки: 32 (24 на телефоне 30) минус 24, пополам. */
    margin-top: 4px;
    color: ${careerColor.text2};

    ${mobile} {
      margin-top: 3px;
    }
  }
`;

const PlanMeta = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

export const PlanText = styled.p`
  margin: 0;
  max-width: 70ch;
  color: ${careerColor.text};
  ${careerType.body()};
`;

export const PlanCta = styled(Link)`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${careerLayout.primaryButton}px;
  margin-top: 8px;
  padding: 0 24px;
  border-radius: ${careerLayout.controlRadius}px;
  /* Единственная насыщенная жёлтая заливка на экране. Жёлтый — только фон. */
  background: ${Colors.main};
  color: ${CTA_TEXT};
  text-align: center;
  ${careerType.bodyStrong()};

  &:hover,
  &:focus-visible {
    color: ${CTA_TEXT};
    background: ${Colors.main_dark};
  }

  ${mobile} {
    width: 100%;
  }
`;
